import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import autoBind from 'react-autobind';
import classnames from 'classnames';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { reduxForm, Field } from 'redux-form';

import FormError from 'ui/common/form/form-error';
import { withApollo } from 'react-apollo';
import TextField from 'ui/common/form/fields/text-field';
import ImageEditorField from 'ui/common/form/fields/image-editor-field';
import validator from 'ui/common/form/validator';
import gql from 'graphql-tag';
import { resultHasErrors, errorsToSingleMessage } from 'utils/graphql-errors';
import csc from 'country-state-city';
import LocalMultiAutoCompleteField from 'ui/common/form/fields/local-multi-auto-complete-field';

import fields, { fieldNames, socialLinkModel } from './model';
import * as messages from './messages';
import {
  selectCurrentUser,
  selectCurrentUsername,
  selectCurrentUserHasAutoGeneratedUsername,
} from 'store/features/auth/selectors';
import { updateCurrentUser } from 'store/features/auth/actions';
import { addMessage } from 'store/features/common/flash/actions';
import { messageTypes } from 'store/features/common/flash/builder';
import SimpleSelect from 'ui/common/form/fields/simple-select-field';

export const FORM_NAME = 'update-basic-info-form';
const usernamePrefix = 'https://indorse.io/';
const rootValidator = validator(fields);

function validate(values) {
  let errors = rootValidator(values) || {};
  errors[fieldNames.socialLinks] = (values[fieldNames.socialLinks] || []).map(socialLink =>
    validator(socialLinkModel(socialLink ? socialLink.type : null))(socialLink)
  );
  return errors;
}

class BasicInfoForm extends Component {
  constructor(props) {
    super(props);

    autoBind(this);
    this.state = {
      educationState: null,
      selectedCountry: null,
    };
  }

  componentDidMount() {
    this.getEducationState();

    this.setState({ selectedCountry: this.props.initialValues.location });
  }

  getCountryOptions() {
    return csc.getAllCountries().map(country => ({
      label: country.name,
      value: country.id,
      name: country.name,
    }));
  }

  async getEducationState() {
    const { client } = this.props;
    try {
      const result = await client.query({
        query: gql`
          query {
            __type(name: "HighestEducation") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });

      const { data, errors } = result;
      if (resultHasErrors({ errors })) {
        addMessage({
          kind: messageTypes.danger,
          content: errorsToSingleMessage({ errors }),
        });
      } else {
        const { __type: { enumValues } } = data;
        this.setState({
          educationState: enumValues,
        });
      }
    } catch (error) {
      addMessage({
        kind: messageTypes.danger,
        content: 'Something went wrong',
      });
    }
  }

  render() {
    const {
      currentUserUsername,
      currentUserHasAutoGeneratedUsername,
      currentUserImageUrl,
      className,
      handleSubmit,
      asyncValidating,
      form: formName,
      intl: { formatMessage },
    } = this.props;

    const { educationState } = this.state;

    const countryOptions = this.getCountryOptions();

    const educationOptions =
      educationState &&
      educationState.map(state => {
        return {
          text: (state.name.charAt(0) + state.name.slice(1).toLowerCase()).replace(/_/g, ' '),
          value: state.name,
        };
      });

    return (
      <div className={className}>
        <form title={formatMessage(messages.header.title)} onSubmit={handleSubmit}>
          <div className={classnames('row align-items-center')}>
            <div className="col-6">
              <FormError formName={formName} />
              <div className="p-3">
                <center>
                  <Field
                    name={fieldNames.img}
                    component={ImageEditorField}
                    label={formatMessage(messages.labels.img)}
                    className="pt-3"
                    defaultPreviewSrc={currentUserImageUrl}
                    previewProps={{
                      editorHeight: 160,
                      editorWidth: 160,
                      borderRadius: 160,
                    }}
                  />
                </center>
              </div>
            </div>
            <div className="col-6">
              {(!currentUserUsername || currentUserHasAutoGeneratedUsername) && (
                <div>
                  <Field
                    name={fieldNames.username}
                    component={TextField}
                    label={formatMessage(messages.labels.username)}
                    prefix={usernamePrefix}
                    suffix={
                      asyncValidating === fieldNames.username && (
                        <i className="fa fa-spin fa-spinner" />
                      )
                    }
                  />
                </div>
              )}
              <div className="p-3">
                <Field
                  name={fieldNames.name}
                  component={TextField}
                  label={formatMessage(messages.labels.name)}
                />
              </div>

              <div className="p-3">
                <Field
                  name={fieldNames.aggregateExperience}
                  component={TextField}
                  label="Years of Experience"
                  min="0"
                  type="number"
                  onKeyPress={event =>
                    event.charCode === 8 || event.charCode === 0 || event.charCode === 13
                      ? null
                      : event.charCode >= 48 && event.charCode <= 57}
                />
              </div>
              <div className="p-3">
                <Field
                  name={fieldNames.education}
                  component={SimpleSelect}
                  options={educationOptions}
                  label="Highest Education"
                />
              </div>

              <div className="p-3">
                <Field
                  name={fieldNames.country}
                  component={LocalMultiAutoCompleteField}
                  options={countryOptions}
                  label={formatMessage(messages.labels.country)}
                  apiResponseConfig={{
                    valueKey: 'value',
                  }}
                  isMulti={false}
                  returnAsObj={true}
                  onChange={selected => this.setState({ selectedCountry: selected })}
                />
              </div>
            </div>
            <div className="col-12">
              <div className="p-3">
                <Field
                  name={fieldNames.bio}
                  component={TextField}
                  label={formatMessage(messages.labels.bio)}
                  multiLine
                  rows={3}
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  }
}

function mapStateToProps(state, ownProps) {
  const currentUser = selectCurrentUser(state);
  const currentUserUsername = selectCurrentUsername(state);
  const currentUserHasAutoGeneratedUsername = selectCurrentUserHasAutoGeneratedUsername(state);
  const {
    name,
    bio,
    imgUrl,
    position,
    yearsOfExperience,
    employmentStatus,
    highestEducation,
    aggregateExperience,
    location,
  } = currentUser;

  let initialCountry = location && csc.getCountryById(location);

  initialCountry = initialCountry && {
    label: initialCountry.name,
    value: initialCountry.id,
    name: initialCountry.name,
  };

  const originalOnSubmit = ownProps ? ownProps.onSubmit : null;
  let onSubmit = originalOnSubmit;

  if (originalOnSubmit) {
    const modalSubmit = values => {
      if (currentUserUsername) delete values[fieldNames.username];

      originalOnSubmit(values);
    };

    onSubmit = modalSubmit;
  }

  return {
    initialValues: {
      name,
      bio,
      img: imgUrl,
      position,
      yearsOfExperience: (yearsOfExperience || '').toString(), // VERY IMPORTANT, don't revert back to number
      employmentStatus,
      education: highestEducation,
      aggregateExperience,
      country: initialCountry,
    },
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    currentUserUsername,
    currentUserHasAutoGeneratedUsername,
    currentUserImageUrl: imgUrl,
    onSubmit,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    addMessage: bindActionCreators(addMessage, dispatch),
    updateCurrentUser: {
      request: bindActionCreators(updateCurrentUser.request, dispatch),
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: FORM_NAME,
    validate,
  })(injectIntl(withApollo(BasicInfoForm)))
);



// WEBPACK FOOTER //
// ./src/ui/graphql/UpdateBasicInfo/form.js